import { PlayerRef } from '@remotion/player';
import {resolveBgmDuration} from '@waka/compat/bgm-duration';
import { TimelineWriteOnlyContext } from '@/features/remotion-editor/editor/context-provider';
import { makeContentItem } from '@/features/remotion-editor/editor/items/make-item-wkwk';
import { CLIP_TYPES } from '@/shared/apis/editor/clip-types';
import { DtoProjectType } from '@/shared/constants';
import { TrackType } from '@/features/remotion-editor/editor/state/types';
import { BgmTrackPlacement } from './bgm-track-state';
import { generateRandomId } from '@/features/remotion-editor/editor/utils/generate-random-id';
import { captureInsertionTarget } from '@waka/compat/incoming-media';
import { commitIncomingMedia } from '@waka/compat/commit-incoming-media';
import type {BgmCatalogItem} from '@waka/compat/bgm-catalog';

interface AddBgmAssetToTimelineParams {
  file: Blob;
  contentId: string;
  filename: string;
  contentPublicUri: string;
  durationInMilliseconds: number|null;
  timelineWriteContext: TimelineWriteOnlyContext;
  playerRef: React.RefObject<PlayerRef | null>;
  fps: number;
  compositionWidth: number;
  compositionHeight: number;
  tracks: TrackType[];
  projectType: DtoProjectType;
  timelineStartFrame?: number;
  extension?: string | null;
  placement: BgmTrackPlacement;
  wakaBgmSource?:BgmCatalogItem['wakaBgmSource'];
}

export const addBgmAssetToTimeline = async ({
  file,
  contentId,
  filename,
  contentPublicUri,
  durationInMilliseconds,
  timelineWriteContext,
  playerRef,
  fps,
  compositionWidth,
  compositionHeight,
  tracks,
  projectType,
  timelineStartFrame,
  extension = null,
  placement,
  wakaBgmSource,
}: AddBgmAssetToTimelineParams) => {
  if(wakaBgmSource&&wakaBgmSource.url!==contentPublicUri)throw new Error('배경음 원본 주소가 일치하지 않습니다.');
  const captured = captureInsertionTarget(
    tracks,
    placement.type === 'existing'
      ? placement.trackIndex
      : placement.insertAtIndex,
    placement.type === 'new',
  );
  const target =
    captured.type === 'new' ? { ...captured, bgm: true } : captured;
  const from = timelineStartFrame ?? playerRef.current?.getCurrentFrame() ?? 0;
  const actualDuration = await resolveBgmDuration(file,durationInMilliseconds);
  const { item, asset } = await makeContentItem({
    file,
    assetId: 'media-' + generateRandomId(20),
    clip: {
      clipType: CLIP_TYPES.AUDIO,
      contentPublicUri,
      videoStart: 0,
      videoEnd: actualDuration,
    },
    playerRef,
    dropPosition: null,
    fps,
    compositionWidth,
    compositionHeight,
    filename,
    remoteUrl: contentPublicUri,
    remoteFileKey: null,
    projectType,
    timelineStartFrame: from,
  });

  asset.isAsset = true;
  asset.extension = extension;
  if (/^\d+$/.test(contentId)) asset.contentId = Number(contentId);
  commitIncomingMedia(timelineWriteContext, item, {...asset,...(wakaBgmSource?{wakaBgmSource}: {})}, target, fps);
};
